import type { UserPublic } from "@/client"
import { ColorModeButton } from "@/components/ui/color-mode"
import { useColorModeValue } from "@/components/ui/color-mode"
import { isAllOrgs, useOrganization } from "@/contexts/OrganizationContext"
import { useSidebar } from "@/contexts/SidebarContext"
import useAuth from "@/hooks/useAuth"
import { colors, effects } from "@/theme"
import { resolveLogoUrl } from "@/utils"
import { Box, Flex, Image, Separator, Text } from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { FiLogOut } from "react-icons/fi"
import LogoIconLight from "/qdat-icon-light.svg"
import LogoIcon from "/qdat-icon.svg"
import LogoLight from "/qdat-logo-light.svg"
import Logo from "/qdat-logo.svg"
import { LanguageSwitcher } from "./LanguageSwitcher"
import { OrganizationDropdown } from "./OrganizationDropdown"
import SidebarItems from "./SidebarItems"

interface CollapsibleSidebarProps {
  onClose?: () => void
}

export const CollapsibleSidebar = ({ onClose }: CollapsibleSidebarProps) => {
  const { isCollapsed, sidebarWidth, setSidebarWidth } = useSidebar()
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const { logout } = useAuth()

  const isLight = useColorModeValue(true, false)

  const handleMouseEnter = () => {
    if (isCollapsed) {
      setSidebarWidth(275)
    }
  }

  const handleMouseLeave = () => {
    if (isCollapsed) {
      setSidebarWidth(120)
    }
  }

  const currentLogo = isLight ? LogoLight : Logo
  const currentIcon = isLight ? LogoIconLight : LogoIcon
  const { currentOrganization, rootOrganization } = useOrganization()

  // Root org (company-level) logo — only shown when a hierarchy is configured
  const rootLogoUrl = resolveLogoUrl(rootOrganization?.logo_url)
  const rootName = rootOrganization?.name ?? null

  // Current department logo — shown when the selected org has a logo and is not the root
  const deptLogoUrl =
    currentOrganization && !isAllOrgs(currentOrganization)
      ? resolveLogoUrl(currentOrganization.logo_url)
      : null
  const deptName =
    currentOrganization && !isAllOrgs(currentOrganization)
      ? currentOrganization.name
      : null

  // When there's no hierarchy, just show the selected org (backward compat)
  const showRootSection = !!rootOrganization && (!!rootLogoUrl || !!rootName)
  const showDeptSection =
    !!deptName &&
    (!rootOrganization || currentOrganization?.id !== rootOrganization?.id)

  const sidebarBg = isLight
    ? "linear-gradient(111.84deg, rgba(255, 255, 255, 0.94) 59.3%, rgba(247, 248, 252, 0.8) 100%)"
    : effects.glass.sidebarBg

  const scrollThumbColor = isLight
    ? "rgba(0, 0, 0, 0.1)"
    : colors.glass.whiteAlpha[100]
  const scrollThumbHover = isLight
    ? "rgba(0, 0, 0, 0.2)"
    : colors.glass.whiteAlpha[200]
  const separatorColor = isLight ? "#E2E8F0" : colors.glass.whiteAlpha[100]
  const nameColor = isLight ? "#1a202c" : "white"
  const emailColor = isLight ? "#718096" : "gray.400"
  const logoutColor = isLight ? "#718096" : "gray.400"
  const logoutHoverBg = isLight
    ? "rgba(0, 0, 0, 0.04)"
    : colors.glass.whiteAlpha[100]
  const subtleColor = isLight ? "#718096" : "gray.400"

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      position="fixed"
      left="0"
      top="0"
      h="100vh"
      w={`${sidebarWidth}px`}
      transition="all 0.2s linear"
      bg={sidebarBg}
      backdropFilter="blur(30px)"
      m="10px"
      maxH="calc(100% - 20px)"
      borderRadius="16px"
      overflowY="auto"
      overflowX="hidden"
      zIndex={99}
      ps="20px"
      pe="20px"
      border={isLight ? "1px solid #E2E8F0" : "none"}
      boxShadow={isLight ? "0 4px 20px rgba(0,0,0,0.06)" : "none"}
      css={{
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: scrollThumbColor,
          borderRadius: "8px",
          "&:hover": {
            background: scrollThumbHover,
          },
        },
      }}
    >
      <Flex direction="column" h="100%">
        {/* QDat Logo */}
        <Box pt="25px" mb="12px">
          <Flex
            align="center"
            justify="center"
            mb={isCollapsed ? "32px" : "16px"}
            position="relative"
          >
            <Image
              src={isCollapsed ? currentIcon : currentLogo}
              alt="QDat logo"
              h={isCollapsed ? "40px" : "32px"}
              w={isCollapsed ? "40px" : "auto"}
              transition="all 0.2s linear"
            />
          </Flex>

          {!isCollapsed && (showRootSection || showDeptSection) && (
            <Flex direction="column" align="center" mb="16px" gap={3} px={2}>
              {/* Organization (company) logo + name */}
              {showRootSection && (
                <Flex direction="column" align="center" gap={1} w="100%">
                  {rootLogoUrl && (
                    <Image
                      src={rootLogoUrl}
                      alt={`${rootName} logo`}
                      maxH="48px"
                      maxW="100%"
                      objectFit="contain"
                      borderRadius="6px"
                    />
                  )}
                  {rootName && (
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      color={nameColor}
                      textAlign="center"
                      wordBreak="break-word"
                      letterSpacing="tight"
                    >
                      {rootName}
                    </Text>
                  )}
                </Flex>
              )}

              {/* Divider between org and dept logos when both are shown */}
              {showRootSection && showDeptSection && (
                <Separator borderColor={separatorColor} w="60%" opacity={0.5} />
              )}

              {/* Department logo + name */}
              {showDeptSection && (
                <Flex direction="column" align="center" gap={1} w="100%">
                  {showRootSection && (
                    <Text
                      fontSize="2xs"
                      color={subtleColor}
                      textTransform="uppercase"
                      letterSpacing="widest"
                      fontWeight="600"
                    >
                      Department
                    </Text>
                  )}
                  {deptLogoUrl && (
                    <Image
                      src={deptLogoUrl}
                      alt={`${deptName} logo`}
                      maxH="40px"
                      maxW="100%"
                      objectFit="contain"
                      borderRadius="6px"
                    />
                  )}
                  {deptName && (
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      color={nameColor}
                      textAlign="center"
                      wordBreak="break-word"
                      letterSpacing="tight"
                    >
                      {deptName}
                    </Text>
                  )}
                </Flex>
              )}
            </Flex>
          )}

          <Separator borderColor={separatorColor} />
        </Box>

        {/* Department Selector */}
        <Box mb={4}>
          <OrganizationDropdown collapsed={isCollapsed} />
        </Box>

        {/* Navigation Items */}
        <Box flex="1" mb="20px">
          <SidebarItems collapsed={isCollapsed} onClose={onClose} />
        </Box>

        {/* App version — sits above the user-section divider */}
        {!isCollapsed && (
          <Text
            fontSize="xl"
            color={emailColor}
            textAlign="left"
            px={3}
            pb={2}
          >
            QDat.io v{__APP_VERSION__}
          </Text>
        )}

        {/* User section at bottom */}
        {currentUser && (
          <Box borderTop="1px solid" borderColor={separatorColor} pt={4} pb={4}>
            <Flex direction="column" gap={3} px={3}>
              {!isCollapsed && (
                <Box minW={0}>
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    color={nameColor}
                    wordBreak="break-word"
                  >
                    {currentUser.full_name || currentUser.email}
                  </Text>
                  {currentUser.full_name && (
                    <Text
                      fontSize="xs"
                      color={emailColor}
                      wordBreak="break-word"
                    >
                      {currentUser.email}
                    </Text>
                  )}
                </Box>
              )}
              <Flex
                align="center"
                gap={1}
                direction={isCollapsed ? "column" : "row"}
                justify={isCollapsed ? "center" : "flex-start"}
              >
                <LanguageSwitcher collapsed={isCollapsed} />
                <ColorModeButton
                  size="sm"
                  color={logoutColor}
                  _hover={{ color: nameColor, bg: logoutHoverBg }}
                />
                <Box
                  as="button"
                  onClick={() => logout()}
                  p={2}
                  borderRadius="md"
                  color={logoutColor}
                  _hover={{ color: nameColor, bg: logoutHoverBg }}
                  transition="all 0.2s"
                  title="Logout"
                >
                  <FiLogOut size={18} />
                </Box>
              </Flex>
            </Flex>
          </Box>
        )}
      </Flex>
    </Box>
  )
}
